import { create } from 'zustand';
import { ProduitCatalogue, ProduitCatalogueRequestDTO } from '../types';
import * as produitCatalogueService from '../services/produitCatalogueService';

interface ProduitCatalogueStore {
  produits: ProduitCatalogue[];
  isLoading: boolean;
  error: string | null;

  // Actions
  loadProduits: () => Promise<void>;
  getProduitById: (id: number) => ProduitCatalogue | undefined;
  addProduit: (data: ProduitCatalogueRequestDTO) => Promise<void>;
  updateProduit: (id: number, data: ProduitCatalogueRequestDTO) => Promise<void>;
  deleteProduit: (id: number) => Promise<void>;
  searchProduits: (keyword: string) => Promise<ProduitCatalogue[]>;
  getProduitsByCategorie: (categorie: string) => Promise<ProduitCatalogue[]>;
  getProduitsByTypeConsommable: (type: string) => Promise<ProduitCatalogue[]>;
  clearProduits: () => void;
}

export const useProduitCatalogueStore = create<ProduitCatalogueStore>((set, get) => ({
  produits: [],
  isLoading: false,
  error: null,

  loadProduits: async () => {
    set({ isLoading: true, error: null });
    try {
      const produits = await produitCatalogueService.getAllProduits();
      set({ produits, isLoading: false });
    } catch (error: any) {
      console.error('Erreur lors du chargement des produits:', error);
      set({ error: error.message || 'Erreur lors du chargement des produits', isLoading: false });
    }
  },

  getProduitById: (id: number) => {
    return get().produits.find((p) => p.id === id);
  },

  addProduit: async (data: ProduitCatalogueRequestDTO) => {
    set({ isLoading: true, error: null });
    try {
      const newProduit = await produitCatalogueService.createProduit(data);
      set((state) => ({
        produits: [...state.produits, newProduit],
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Erreur lors de la création du produit:', error);
      set({ error: error.message || 'Erreur lors de la création du produit', isLoading: false });
      throw error;
    }
  },

  updateProduit: async (id: number, data: ProduitCatalogueRequestDTO) => {
    set({ isLoading: true, error: null });
    try {
      const updatedProduit = await produitCatalogueService.updateProduit(id, data);
      set((state) => ({
        produits: state.produits.map((p) => (p.id === id ? updatedProduit : p)),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du produit:', error);
      set({ error: error.message || 'Erreur lors de la mise à jour du produit', isLoading: false });
      throw error;
    }
  },

  deleteProduit: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await produitCatalogueService.deleteProduit(id);
      set((state) => ({
        produits: state.produits.filter((p) => p.id !== id),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Erreur lors de la suppression du produit:', error);
      set({ error: error.message || 'Erreur lors de la suppression du produit', isLoading: false });
      throw error;
    }
  },

  searchProduits: async (keyword: string) => {
    try {
      const results = await produitCatalogueService.searchProduits(keyword);
      return results;
    } catch (error: any) {
      console.error('Erreur lors de la recherche de produits:', error);
      throw error;
    }
  },

  getProduitsByCategorie: async (categorie: string) => {
    try {
      const results = await produitCatalogueService.getProduitsByCategorie(categorie);
      return results;
    } catch (error: any) {
      console.error('Erreur lors de la recherche par catégorie:', error);
      throw error;
    }
  },

  getProduitsByTypeConsommable: async (type: string) => {
    try {
      const results = await produitCatalogueService.getProduitsByTypeConsommable(type);
      return results;
    } catch (error: any) {
      console.error('Erreur lors de la recherche par type consommable:', error);
      throw error;
    }
  },

  clearProduits: () => {
    set({ produits: [], error: null });
  },
}));
